import {
  AgxButton,
  AgxSpacer,
  AgxTooltip,
  AgxTypography,
  Icon_PlusOutline,
  Icon_TrashOutline,
} from '@urbanx/agx.ui-components2';
import { tokens } from '@urbanx/agx.ui-components2/tokens';
import type { ReactElement, ReactNode } from 'react';
import 'components/settings-section/SettingsSection.scss';
import './LineItems.scss';

export const LineItemRow = ({
  fields,
  label,
  action,
}: {
  fields: ReactElement[];
  label: string;
  action?: ReactNode;
}) => (
  <AgxSpacer fill className="agx-line-items-row-container">
    <AgxSpacer
      direction="horizontal"
      wrap
      gap="spacing-md"
      contentAlignment="start"
      role="group"
      aria-label={label}
    >
      {fields.map((field) => (
        <AgxSpacer key={field.key} className="agx-line-items-field">
          {field}
        </AgxSpacer>
      ))}
      <AgxSpacer
        contentAlignment="center"
        contentJustification="center"
        className={action ? 'agx-line-items-remove-action' : 'agx-line-items-action-space'}
        aria-hidden={action ? undefined : true}
      >
        {action}
      </AgxSpacer>
    </AgxSpacer>
  </AgxSpacer>
);

export const LineItems = ({
  count,
  maximumCount,
  limitMessage,
  errorText,
  noun,
  rowLabel,
  disabled,
  onAdd,
  onRemove,
  renderFields,
  emptyLabel,
}: {
  count: number;
  maximumCount: number;
  limitMessage: string;
  errorText?: string;
  noun: string;
  rowLabel: string;
  disabled: boolean;
  onAdd(): void;
  onRemove(index: number): void;
  renderFields(index: number): ReactElement[];
  emptyLabel?: string;
}) => (
  <AgxSpacer gap="spacing-lg" fill className="agx-line-items">
    {errorText && (
      <AgxTypography type="form_error" role="alert">
        {errorText}
      </AgxTypography>
    )}
    {Array.from({ length: count }, (_, index) => (
      <LineItemRow
        key={index}
        label={`${rowLabel} ${index + 1}`}
        fields={renderFields(index)}
        action={
          <AgxButton
            id={`remove-${noun.replaceAll(' ', '-')}-${index + 1}`}
            text={<Icon_TrashOutline color={tokens.fg.tertiary} width={18} height={18} />}
            ariaLabel={`Remove ${noun} ${index + 1}`}
            size="medium"
            variant="naked"
            onClick={() => onRemove(index)}
            disabled={disabled}
          />
        }
      />
    ))}
    <AgxSpacer
      contentAlignment={count === 0 ? 'center' : 'start'}
      contentJustification={count === 0 ? 'center' : 'start'}
      gap="spacing-md"
      fill
      className={
        count === 0 ? 'agx-settings-empty-content agx-line-items-empty-add-action' : undefined
      }
    >
      {count === 0 && emptyLabel && (
        <AgxTypography type="body_ui" size="small" color={tokens.text.secondary}>
          {emptyLabel}
        </AgxTypography>
      )}
      <AgxTooltip
        id={`add-${noun.replaceAll(' ', '-')}-limit`}
        content={limitMessage}
        contentClassName="agx-line-items-limit-tooltip"
        portaled
        disabled={count < maximumCount}
        triggerTabIndex={count >= maximumCount ? 0 : undefined}
        triggerAriaLabel={count >= maximumCount ? `Add ${noun}: limit reached` : undefined}
      >
        <AgxButton
          id={`add-${noun.replaceAll(' ', '-')}`}
          text={`Add ${noun}`}
          variant="primary"
          size="medium"
          leftIcon={<Icon_PlusOutline color="currentColor" width={18} height={18} />}
          onClick={() => {
            if (!disabled && count < maximumCount) onAdd();
          }}
          disabled={disabled || count >= maximumCount}
        />
      </AgxTooltip>
    </AgxSpacer>
  </AgxSpacer>
);
